<script>
import Layout from '@layouts/adminLayout.vue'
import MottoIcon from '@components/motto-icon.vue'
import LoaderIcon from '@components/loader-icon.vue'
import LeaderboardPosition from '@components/leaderboards/leaderboard-position.vue'
import ScrollerSelector from '@components/scroller-selector.vue'
import { downloadLeaderboardList, getLeaderboardDataForPeriod, getPeriods } from '@src/api/leaderboards'
import {
  transformManyPeriods as transformPeriods,
  transformManyLeaderboardData as transformLeaderboardData,
} from '@src/api/transformers/leaderboardTransformer'
import _ from 'lodash'
import downloadBlob from '@utils/download-blob'
import { authComputed } from '@state/helpers'

export default {
  page: {
    title: 'Leaderboards',
    meta: [
      {
        name: 'description',
        content: 'The Leaderboards page.',
      },
    ],
  },
  components: {
    Layout,
    LoaderIcon,
    MottoIcon,
    LeaderboardPosition,
    ScrollerSelector,
  },
  data() {
    return {
      loading: true,
      error: false,
      loadingLeaderboard: true,
      leaderboardData: [],
      periods: [],
      selectedPeriod: '',
      selectedLeaderboardCategory: '',
    }
  },
  computed: {
    ...authComputed,
    getLeaderboardCategories() {
      return _.keys(this.leaderboardData)
    },
    getPeriods() {
      const sortedPeriods = _.orderBy(this.periods, ['timestamp'], ['asc'])
      return sortedPeriods.map(({ label }) => label)
    },
    getLeaderboardDataByCategory() {
      return this.leaderboardData[this.getSelectedLeaderboardCategory]
    },
    getSelectedLeaderboardCategory() {
      return this.selectedLeaderboardCategory || this.getLeaderboardCategories[0]
    },

    selectedPeriodApiString() {
      return _.find(this.periods, { label: this.selectedPeriod }).rawApiPeriod
    },
  },
  created() {
    this.fetchLeaderboardsInfo()
  },
  methods: {
    async fetchLeaderboardsInfo() {
      try {
        const {
          data: { data: periods },
        } = await getPeriods()
        this.periods = transformPeriods(periods)
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    selectPeriod(period) {
      this.selectedPeriod = period
      this.loadSelectedPeriod()
    },

    async loadSelectedPeriod() {
      try {
        this.loadingLeaderboard = true
        const selectedPeriodApiString = this.selectedPeriodApiString
        const {
          data: { data: leaderboardData },
        } = await getLeaderboardDataForPeriod(selectedPeriodApiString)
        this.leaderboardData = transformLeaderboardData(leaderboardData)
      } catch (err) {
        this.error = true
      } finally {
        this.loadingLeaderboard = false
      }
    },

    selectLeaderboardCategory(leaderboardCategory) {
      this.selectedLeaderboardCategory = leaderboardCategory
    },

    getDownloadFileName(period, type) {
      return _.kebabCase(`leaderboard-list-${period}-${type}`)
    },

    async downloadList() {
      try {
        const { data: leaderboardListData } = await downloadLeaderboardList({
          period: this.selectedPeriodApiString,
          leaderboardType: this.getSelectedLeaderboardCategory,
        })
        downloadBlob(
          leaderboardListData,
          this.getDownloadFileName(this.selectedPeriod, this.getSelectedLeaderboardCategory),
          'application/pdf'
        )
      } catch (err) {
        this.$bvToast.toast('There was an error downloading this list. Please try again later', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.viewContainer">
      <LoaderIcon v-if="loading || error" :error="error"></LoaderIcon>
      <div v-else>
        <div :class="$style.leaderboardHeader" class="d-flex justify-content-between mb-3">
          <BaseTitle class="color-primary-blue mb-0">Leaderboards</BaseTitle>
          <RouterLink
            :class="$style.myRankingsButtonWrapper"
            class="text-decoration-none"
            :to="{ name: 'my-rankings' }">
            <BaseButton :class="$style.myRankingsButton">
              <MottoIcon icon-color="white" name="TrophyFill" :skip-filter="true"></MottoIcon>
              <p class="button-text color-white">My Rankings</p>
            </BaseButton>
          </RouterLink>
        </div>
        <ScrollerSelector
          :class="$style.scrollerPeriodSelector"
          :items="getPeriods"
          :start-at-end="true"
          @updateScrollerItem="selectPeriod">
          <template v-slot="{ currentScrollerItem }">
            <h2 data-current-period :class="$style.currentScrollerItem">{{ currentScrollerItem }}</h2>
          </template>
        </ScrollerSelector>
        <LoaderIcon v-if="loadingLeaderboard"></LoaderIcon>
        <div v-if="!loadingLeaderboard">
          <div class="d-flex justify-content-between">
            <BaseSelect
              v-model="selectedLeaderboardCategory"
              :class="$style.leaderboardCategoriesSelect"
              name="leaderboardCategory"
              :options="getLeaderboardCategories"
              data-leaderboard-category-selector
              @update="selectLeaderboardCategory" />
            <BaseButton theme="white" :class="$style.downloadListButton" @click="downloadList">
              <MottoIcon name="Download" icon-color="gray" />
              Download list
            </BaseButton>
          </div>
          <div :class="$style.leaderboardContainer">
            <LeaderboardPosition
              v-for="(data, index) in getLeaderboardDataByCategory"
              :key="index"
              :leaderboard-data="data"
              :leaderboard-position="data.rank"
              :highlight="data.rank < 4" />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.leaderboardCategoriesSelect {
  width: 302px;
  margin-right: 24px;
}

.viewContainer {
  width: auto;
}

.leaderboardHeader {
  @media (max-width: $lg-breakpoint) {
    align-items: center;
    justify-content: space-between;

    h1 {
      margin-right: 20px;
    }
  }
}

.myRankingsButtonWrapper {
  @media (max-width: $lg-breakpoint) {
    flex: 0 1 180px;
    width: 100%;
    min-width: 0;
  }
}

.myRankingsButton {
  @media (max-width: $lg-breakpoint) {
    flex: 0 1 180px;
    width: 100%;
    min-width: 0;
  }

  display: flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  min-width: 180px;
  height: 40px;

  > div {
    margin-right: 12px;
  }

  p {
    white-space: nowrap;
  }
}

.leaderboardContainer {
  margin-top: 32px;

  > div {
    margin-bottom: 16px;
  }
}

.scrollerPeriodSelector {
  width: fit-content;
  margin: 40px auto;
}

.currentScrollerItem {
  @media (max-width: $lg-breakpoint) {
    min-width: 150px;
    font-size: 16px;
    color: $primary-blue;
  }

  min-width: 210px;
  margin: 0;
  color: $dark-gray;
  text-align: center;
}

.downloadListButton {
  @media (max-width: $lg-breakpoint) {
    width: min-content;
    padding: 0 12px 0 0;
    text-decoration: underline;
    white-space: nowrap;
    border: none;

    svg {
      display: none;
    }
  }

  display: flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 40px;

  svg {
    margin-right: 13px;
  }
}
</style>
